import {
  CheckOutlined,
  QuestionCircleOutlined,
  TeamOutlined,
  UsergroupDeleteOutlined,
} from '@ant-design/icons';
import { Button } from '@mm/components';
import { AccordionAPI } from '@mm/components/src/AccordionItem/AccordionItem';
import { landmineNames } from '@mm/constants/landmine-names';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';
import { HubspotIcon } from '@mm/icons';
import { useHubspotCRUDActions } from '@mm/state/actions/HubspotCRUDActions';
import {
  HubspotAccessToken,
  HubspotOauthToken,
} from '@mm/state/model/ConnectionModel';
import { selectHubspotConnections } from '@mm/state/modules/connectionsTableState/selectors';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { selectHubspotSuppressionList } from '@mm/state/modules/suppressionListsTableState/selectors';
import { selectCrmsLeadsSent } from '@mm/state/selectors/connectionsSelectors';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ButtonColor } from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { HelpCenter } from '../../../../../../../../common/components/HelpCenter/HelpCenter';
import { BaseIntegration } from '../../../BaseIntegration/BaseIntegration';
import { BaseIntegrationHeader } from '../../../BaseIntegration/BaseIntegrationHeader';
import { KlaviyoErrorMessage } from '../KlaviyoIntegrations/KlaviyoErrorMessage';
import { HubspotApiKeyConnection } from './HubspotApiKeyConnection';
import styles from './HubspotIntegrations.module.css';
import { HubspotSettings } from './HubspotSettings';
import { HubspotSuppressionSetup } from './HubspotSuppressionSettings';
import { useConnectHubspot } from './use-connect-hubspot';

const helpLink =
  'https://customers.ai/help/l/en/article/st7sh4nntk-hub-spot-draft';

const syncWarning = (showSyncWarning: boolean) => {
  if (showSyncWarning) {
    return (
      <KlaviyoErrorMessage
        classNames={styles.syncWarning}
        error="Not Syncing"
        iconSize="16px"
      />
    );
  }
  return null;
};

const leadsSentCounter = (crmsLeadsSent?: number) => {
  if (crmsLeadsSent) {
    return (
      <div className="integrations__header-status">
        <TeamOutlined className="integrations__header-status-icon" />
        <div className="integrations__header-status-count">{crmsLeadsSent}</div>
        Leads sent
      </div>
    );
  }
  return null;
};

const apiKeyHeader = (showSyncWarning: boolean, crmsLeadsSent?: number) => (
  <div className="integrations__header-actions">
    <HelpCenter
      helpLink={helpLink}
      title=""
      icon={<QuestionCircleOutlined style={{ fontSize: 20 }} />}
    />
    {syncWarning(showSyncWarning)}
    {leadsSentCounter(crmsLeadsSent)}
    <Button
      css={{ container: 'integrations__header-add-button-secondary' }}
      onClick={e => {
        e?.stopPropagation();
        window.open('https://app.hubspot.com', '_blank');
      }}
      color={ButtonColor.secondaryBlue}
    >
      Add an Account
    </Button>
  </div>
);

const header = (
  readOnly: boolean,
  showSyncWarning: boolean,
  onClickAccessToken: (access_token) => void,
  onClickOauth: () => void,
  version: 'connections' | 'suppression',
  matchingToken?: HubspotAccessToken | HubspotOauthToken,
  leadsSent?: number,
  profilesSuppressed: number = 0,
) => (
  <div className="integrations__header-actions">
    {showSyncWarning && (
      <KlaviyoErrorMessage
        classNames={styles.syncWarning}
        error="Not Syncing"
        iconSize="16px"
      />
    )}
    {!matchingToken ? (
      <Button
        css={{
          container: classNames('integrations__header-add-button-secondary', {
            'integrations__header-add-button-secondary-static-disabled':
              !!readOnly,
          }),
        }}
        type="button"
        disabled={!!readOnly}
        onClick={e => {
          e?.stopPropagation();
          if (readOnly) {
            return;
          }
          onClickOauth();
        }}
        color={ButtonColor.secondaryBlue}
      >
        Connect Hubspot
      </Button>
    ) : (
      <>
        {!!leadsSent && (
          <div className="integrations__header-status">
            <TeamOutlined className="integrations__header-status-icon" />
            <div className="integrations__header-status-count">{leadsSent}</div>
            Leads sent
          </div>
        )}
        {profilesSuppressed > 0 && (
          <div className="integrations__header-status">
            <UsergroupDeleteOutlined className="integrations__header-status-icon" />
            <span className="integrations__header-status-count">
              {profilesSuppressed}
            </span>
            <span>Profiles suppressed</span>
          </div>
        )}

        <Button
          css={{
            container: classNames(
              'integrations__header-add-button-secondary',
              'integrations__header-add-button-secondary-static-disabled',
            ),
          }}
          type="button"
          disabled
          leftIcon={<CheckOutlined />}
          color={ButtonColor.secondaryBlue}
        >
          Signed In
        </Button>
      </>
    )}
  </div>
);

export const HubspotIntegrations = () => {
  const features = useSelector(combinedFeatureFlagsSelector);
  const hasNativeIntegrationsFeature = features.nativeIntegrations;
  const version = 'connections';

  const { openNewBillingModal2023 } = useModalActions();
  const showUpgradeModal = (onSuccessBilling?: () => void) => {
    openNewBillingModal2023({
      landmineName: landmineNames.KLAVIYO_UPGRADE,
      onSuccessBilling,
    });
  };

  const {
    getHubspotAccounts,
    connectOauth,
    getHubspotConnections,
    connectAccessToken,
    updateAccessToken,
    connectionsMessage,
  } = useConnectHubspot();

  const hubspotConnections = useSelector(selectHubspotConnections);
  const suppressions = useSelector(selectHubspotSuppressionList);
  const suppression = suppressions[0];

  const tokens = useSelector((state: StoreState) => state.hubspot?.accounts);
  const hubspotConnection = hubspotConnections?.[0];

  const matchingToken = tokens?.find(
    token => token.id === hubspotConnection?.accessToken?.id,
  );

  const readOnly = !!matchingToken?.readOnly;
  const { invalidateStore } = useHubspotCRUDActions();
  const [accountsFetched, setAccountsFetched] = useState(false);

  const { companyName = 'Customers.ai' } = useWhitelabelConfig();

  const sectionProps = {
    landmineName: landmineNames.HUBSPOT_UPGRADE,
    featureFlag: 'nativeIntegrations',
  };

  const upgradeOrCreateButtonName = 'Connect Hubspot';

  const showSyncWarning =
    version === 'connections' && matchingToken && hubspotConnection
      ? !!(connectionsMessage || matchingToken.errorInfo)
      : false;

  const crmsLeadsSent = useSelector(selectCrmsLeadsSent);
  const leadsSent =
    version === 'connections'
      ? crmsLeadsSent?.hubspotContactsSent
      : suppression?.profilesCount;

  useEffect(() => {
    if (accountsFetched) {
      getHubspotConnections();
    }
  }, [accountsFetched]);

  useEffect(() => {
    (async () => {
      await getHubspotAccounts();
      setAccountsFetched(true);
    })();
    return () => {
      invalidateStore();
    };
  }, []);

  const ref = useRef<AccordionAPI>(null);

  return (
    <BaseIntegration
      openOnMount
      accordionRef={ref}
      titleComponent={
        <BaseIntegrationHeader
          title="Hubspot"
          upgradeButtonName={upgradeOrCreateButtonName}
          subtitle={`Automatically sync your ${companyName} contacts to Hubspot`}
          icon={<HubspotIcon />}
          headerRightComponent={
            matchingToken?.type === 'Hubspot::OauthToken'
              ? header(
                  readOnly,
                  showSyncWarning,
                  connectAccessToken,
                  connectOauth,
                  version,
                  matchingToken,
                  leadsSent,
                  suppression.profilesCount,
                )
              : apiKeyHeader(showSyncWarning, leadsSent)
          }
          css={{ icon: 'integrations__hubspot-header-icon' }}
          {...sectionProps}
        />
      }
      integrationType="hubspot"
    >
      <div className={styles.settingsContent}>
        {matchingToken?.type !== 'Hubspot::OauthToken' && (
          <div>
            <span className={styles.label}>Credentials</span>
            <HubspotApiKeyConnection
              hasNativeIntegrationsFeature={hasNativeIntegrationsFeature}
              helpLink={helpLink}
              controlsDisabled={!!readOnly}
              token={matchingToken}
              createApiKey={connectAccessToken}
              updateApiKey={updateAccessToken}
              showUpgradeModal={showUpgradeModal}
            />
          </div>
        )}
        {matchingToken && (
          <>
            <div>
              <span className={styles.label}>Suppressions</span>
              <HubspotSuppressionSetup tokenId={matchingToken.id} />
            </div>
            <div>
              <span className={styles.label}>Destinations</span>
              <HubspotSettings
                connectionsMessage={connectionsMessage}
                hubspotConnections={hubspotConnections}
                account={matchingToken}
                controlsDisabled={readOnly}
              />
            </div>
          </>
        )}
      </div>
    </BaseIntegration>
  );
};
